import { useEffect, useState } from 'react';
import {
  Text,
  TextInput,
  Button,
  Stack,
  Alert,
  Group,
  ThemeIcon,
  Box,
  Flex,
  Anchor,
  Badge,
  Divider,
  SimpleGrid,
  type BoxProps,
} from '@mantine/core';
import { useForm } from '@mantine/form';
import QRCode from 'qrcode';
import { useSearchParams } from 'react-router-dom';
import {
  IconMail,
  IconCheck,
  IconAlertCircle,
  IconAlertTriangle,
  IconArrowRight,
  IconBrandWindows,
  IconBrandGoogleFilled,
  IconBrandWhatsapp,
  IconClock,
  IconDevices,
  IconFileCheck,
  IconHeadset,
  IconInfoCircle,
  IconPhone,
  IconTicket,
} from '@tabler/icons-react';
import {
  requestCustomerMagicLink,
  getCustomerSsoConfig,
  submitCustomerSsoRequestDetails,
  type CustomerSsoAvailability,
} from '../../lib/api.customer';
import styles from './CustomerLoginPage.module.css';

type State = 'idle' | 'loading' | 'sent' | 'error';

/** Fehlercodes aus /public/customer/verify und /public/customer/sso/... */
const URL_ERROR_MESSAGES: Record<string, string> = {
  missing_token: 'Der Anmelde-Link ist unvollständig. Bitte fordere einen neuen Link an.',
  invalid_token:
    'Der Anmelde-Link ist ungültig oder wurde bereits verwendet. Bitte fordere einen neuen Link an.',
  sso_cancelled: 'Die Anmeldung wurde abgebrochen.',
  sso_no_access:
    'Für dieses Konto ist kein Portal-Zugang eingerichtet. Bitte melde dich mit der E-Mail-Adresse an, die bei uns hinterlegt ist — oder kontaktiere deinen Ansprechpartner bei FixByte.',
  sso_request_rejected:
    'Deine Zugangsanfrage wurde abgelehnt. Wenn du das für einen Fehler hältst, wende dich bitte an deinen Ansprechpartner bei FixByte.',
  sso_no_verified_email:
    'Dein Konto hat keine verifizierte E-Mail-Adresse. Bitte nutze den Anmelde-Link per E-Mail.',
  sso_not_configured: 'Diese Anmeldemethode ist derzeit nicht verfügbar.',
  sso_unknown_provider: 'Diese Anmeldemethode ist derzeit nicht verfügbar.',
  sso_state_expired: 'Die Anmeldung ist abgelaufen. Bitte versuche es erneut.',
  sso_provider_error:
    'Die Anmeldung ist fehlgeschlagen. Bitte versuche es erneut oder nutze den Anmelde-Link per E-Mail.',
};

/** Eine Stufe dunkler als die Markenfarbe: #ff6600 wäre als kleine Linkschrift auf Weiß zu blass. */
const LINK_COLOR = 'cyberOrange.8';

/** Kurzer Titel, Detail darunter — liest sich schneller als vier ganze Sätze. */
const PORTAL_FEATURES = [
  { icon: IconAlertTriangle, title: 'Störungen melden', text: 'Stand live verfolgen' },
  { icon: IconTicket, title: 'Alle Tickets im Blick', text: 'Mit Verlauf und Rückfragen' },
  { icon: IconDevices, title: 'Geräte & Verträge', text: 'Termine inklusive' },
  { icon: IconFileCheck, title: 'Angebote freigeben', text: 'Prüfen und bestätigen' },
];

/** WhatsApp-QR: Handy scannt und landet direkt im Support-Chat. */
function WhatsAppQr({ href }: { href: string }) {
  const [src, setSrc] = useState<string | null>(null);
  useEffect(() => {
    QRCode.toDataURL(href, { width: 240, margin: 1 })
      .then(setSrc)
      .catch(() => setSrc(null));
  }, [href]);
  if (!src) return null;
  return <img src={src} alt="QR-Code für den WhatsApp-Support-Chat" width={104} height={104} />;
}

/**
 * Kopf der Infospalte ab Desktop. Logo + Portal-Kennzeichnung: die Seite wird auch
 * direkt über die Kundenportal-Domain aufgerufen, ohne jeden weiteren Kontext.
 * Mobil übernimmt die Kopfzeile über dem Formular.
 */
function PortalHero(boxProps: BoxProps) {
  return (
    <Stack gap="md" align="flex-start" {...boxProps}>
      <img src="/fixbyte-logo.svg" alt="FixByte IT" style={{ height: 40, width: 'auto' }} />
      <Badge variant="light" color="cyberOrange" size="lg" radius="xl" mt="xl">
        Kundenportal
      </Badge>
      <Text fw={700} fz={40} lh={1.1} style={{ letterSpacing: '-0.02em', textWrap: 'balance' }}>
        Deine IT-Anliegen, <span className={styles.accent}>an einem Ort.</span>
      </Text>
      <Text size="lg" c="dimmed" style={{ textWrap: 'pretty' }}>
        Der Servicebereich für Kundinnen und Kunden von FixByte IT – rund um die Uhr, ohne
        Warteschleife.
      </Text>
    </Stack>
  );
}

function PortalFeatures() {
  return (
    <div>
      <Text size="xs" fw={700} tt="uppercase" c="cyberOrange.9" mb="md" style={{ letterSpacing: '0.06em' }}>
        Was du hier erledigen kannst
      </Text>
      <SimpleGrid cols={{ base: 1, xs: 2 }} spacing="lg" verticalSpacing="lg">
        {PORTAL_FEATURES.map(({ icon: FeatureIcon, title, text }) => (
          <Group key={title} gap="sm" wrap="nowrap" align="flex-start">
            <div className={styles.featureIcon} aria-hidden>
              <FeatureIcon size={20} stroke={1.75} />
            </div>
            <div>
              <Text fw={600} size="sm" lh={1.35}>
                {title}
              </Text>
              <Text size="sm" c="dimmed" lh={1.4}>
                {text}
              </Text>
            </div>
          </Group>
        ))}
      </SimpleGrid>
    </div>
  );
}

function SupportContact({
  support,
  whatsappHref,
}: {
  support: NonNullable<CustomerSsoAvailability['support']>;
  whatsappHref: string | null;
}) {
  return (
    <Box className={styles.contactCard} p="lg">
      <Group gap={8} mb="sm" wrap="nowrap">
        <IconHeadset size={18} color="var(--mantine-color-cyberOrange-7)" />
        <Text fw={600} size="sm">
          Direkt Kontakt aufnehmen
        </Text>
      </Group>
      <Group align="flex-start" justify="space-between" wrap="nowrap" gap="md">
        <Stack gap={8} style={{ flex: 1 }}>
          {support.phone && (
            <Group gap={8} wrap="nowrap">
              <IconPhone size={16} color="var(--mantine-color-cyberOrange-7)" />
              <Anchor
                href={`tel:${support.phone.replace(/[^\d+]/g, '')}`}
                size="sm"
                fw={600}
                c={LINK_COLOR}
              >
                {support.phone}
              </Anchor>
            </Group>
          )}
          {support.email && (
            <Group gap={8} wrap="nowrap">
              <IconMail size={16} color="var(--mantine-color-cyberOrange-7)" />
              <Anchor href={`mailto:${support.email}`} size="sm" c={LINK_COLOR}>
                {support.email}
              </Anchor>
            </Group>
          )}
          {whatsappHref && (
            <Group gap={8} wrap="nowrap">
              <IconBrandWhatsapp size={16} color="var(--mantine-color-teal-6)" />
              <Anchor href={whatsappHref} target="_blank" rel="noreferrer" size="sm" c="teal.7">
                WhatsApp-Chat öffnen
              </Anchor>
            </Group>
          )}
          {support.hours && (
            <Group gap={8} wrap="nowrap" align="flex-start">
              <IconClock size={16} style={{ flexShrink: 0, marginTop: 2 }} />
              <Text size="sm" style={{ whiteSpace: 'pre-line' }}>
                {support.hours}
              </Text>
            </Group>
          )}
          {support.emergencyNote && (
            <Text size="xs" c="dimmed" style={{ whiteSpace: 'pre-line' }}>
              {support.emergencyNote}
            </Text>
          )}
        </Stack>

        {/* Am Handy sinnlos: Man müsste das eigene Display scannen — der Link darüber reicht. */}
        {whatsappHref && (
          <Stack gap={4} align="center" style={{ flexShrink: 0 }} visibleFrom="sm">
            <WhatsAppQr href={whatsappHref} />
            <Text size="xs" c="dimmed" ta="center">
              Scannen für
              <br />
              WhatsApp
            </Text>
          </Stack>
        )}
      </Group>
    </Box>
  );
}

/** Support-Mail nur, wenn keine Kontaktkarte da ist — sonst stünde sie doppelt auf der Seite. */
function LoginHelpFooter({ showSupportEmail, ...boxProps }: { showSupportEmail: boolean } & BoxProps) {
  return (
    <Text size="xs" component="footer" c="dimmed" {...boxProps}>
      Du bist FixByte-Mitarbeiter?{' '}
      <Anchor href="https://servicehub.fixbyte.it" size="xs" underline="always" c={LINK_COLOR}>
        Zum ServiceHub
      </Anchor>
      {showSupportEmail && (
        <>
          <br />
          Probleme beim Anmelden?{' '}
          <Anchor
            href="mailto:support@fixbyte.it"
            size="xs"
            underline="always"
            c={LINK_COLOR}
            fw={600}
          >
            support@fixbyte.it
          </Anchor>
        </>
      )}
    </Text>
  );
}

export function CustomerLoginPage() {
  const [state, setState] = useState<State>('idle');
  const [errorMsg, setErrorMsg] = useState('');
  const [sso, setSso] = useState<CustomerSsoAvailability | null>(null);
  const [searchParams, setSearchParams] = useSearchParams();
  const urlError = searchParams.get('error');
  // main.tsx leitet bei abgelaufener Session mit ?reason=session_ended hierher
  const sessionEnded = searchParams.get('reason') === 'session_ended';
  // Nach dem ersten Klick auf einen Anmeldeknopf sperren: Der Browser verlässt die Seite
  // ohnehin, ein zweiter Klick startet nur eine zweite, konkurrierende Anmeldung.
  const [ssoPending, setSsoPending] = useState<'microsoft' | 'google' | null>(null);
  const startSso = (provider: 'microsoft' | 'google') => (event: React.MouseEvent) => {
    if (ssoPending) {
      event.preventDefault();
      return;
    }
    setSsoPending(provider);
    // Falls die Seite per Zurück-Taste wieder angezeigt wird, nicht dauerhaft gesperrt lassen.
    window.setTimeout(() => setSsoPending(null), 10_000);
  };
  // Zugangsanfrage gestellt = positiver Zustand, keine Fehlermeldung
  const requestPending = urlError === 'sso_request_pending';
  const detailsToken = searchParams.get('req');
  const urlErrorMessage =
    urlError && !requestPending ? (URL_ERROR_MESSAGES[urlError] ?? null) : null;

  // Selbstauskunft (Firma/Name) zur Zugangsanfrage
  const [detailsCompany, setDetailsCompany] = useState('');
  const [detailsName, setDetailsName] = useState('');
  const [detailsState, setDetailsState] = useState<'idle' | 'saving' | 'done' | 'error'>('idle');

  async function handleSubmitDetails() {
    if (!detailsToken || (!detailsCompany.trim() && !detailsName.trim())) return;
    setDetailsState('saving');
    try {
      await submitCustomerSsoRequestDetails({
        token: detailsToken,
        companyName: detailsCompany.trim(),
        personName: detailsName.trim(),
      });
      setDetailsState('done');
    } catch {
      setDetailsState('error');
    }
  }

  useEffect(() => {
    getCustomerSsoConfig()
      .then(setSso)
      .catch(() => setSso(null));
  }, []);

  const form = useForm({
    initialValues: { email: '' },
    validate: {
      email: (v) => (/^\S+@\S+\.\S+$/.test(v) ? null : 'Bitte gib eine gültige E-Mail-Adresse ein'),
    },
  });

  async function handleSubmit(values: { email: string }) {
    setState('loading');
    setErrorMsg('');
    try {
      await requestCustomerMagicLink(values.email);
      setState('sent');
    } catch (err) {
      setErrorMsg(err instanceof Error ? err.message : 'Ein Fehler ist aufgetreten');
      setState('error');
    }
  }

  function dismissReason() {
    searchParams.delete('reason');
    setSearchParams(searchParams, { replace: true });
  }

  function dismissUrlError() {
    searchParams.delete('error');
    searchParams.delete('req');
    setSearchParams(searchParams, { replace: true });
  }

  const showSsoButtons = sso !== null && (sso.microsoft || sso.google);
  // Nur die Anbieter nennen, die wirklich als Knopf auf der Seite stehen
  const ssoProviderNames = [sso?.microsoft && 'Microsoft', sso?.google && 'Google']
    .filter(Boolean)
    .join(' oder ');

  const support = sso?.support;
  const whatsappHref = support?.whatsapp ? `https://wa.me/${support.whatsapp}` : null;
  const hasSupportInfo = Boolean(
    support?.phone || support?.email || support?.hours || whatsappHref
  );

  return (
    // Ab Desktop zweispaltig: Infospalte links, Anmeldung rechts — vorher stand
    // alles in einer 440px-Säule untereinander und die halbe Seite musste man scrollen.
    <Flex direction={{ base: 'column', md: 'row-reverse' }} mih="100dvh" bg="white">
      {/* Anmeldung steht im DOM zuerst: Tastatur und Screenreader landen direkt
          beim Formular, mobil folgt es unmittelbar auf die Kopfzeile. */}
      <Box
        component="main"
        id="customer-login-main"
        flex={{ base: 'none', md: '1 1 50%' }}
        px={{ base: 'lg', sm: 'xl' }}
        py={{ base: 'xl', md: 48 }}
        style={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}
      >
        <Box w="100%" maw={420}>
          <Group justify="space-between" wrap="nowrap" hiddenFrom="md" mb={40}>
            <img src="/fixbyte-logo.svg" alt="FixByte IT" style={{ height: 34, width: 'auto' }} />
            <Badge variant="light" color="cyberOrange" radius="xl">
              Kundenportal
            </Badge>
          </Group>

          {sessionEnded && (
            <Alert
              icon={<IconInfoCircle size={16} />}
              color="gray"
              variant="light"
              radius="md"
              mb="lg"
              withCloseButton
              onClose={dismissReason}
            >
              Du wurdest abgemeldet. Bitte melde dich erneut an.
            </Alert>
          )}

          <div className={styles.fadeUp}>
            {state === 'sent' ? (
              <Stack align="flex-start" gap="md">
                <ThemeIcon size={56} radius="xl" color="teal" variant="light">
                  <IconCheck size={28} />
                </ThemeIcon>
                <h1
                  id="customer-login-heading"
                  style={{
                    fontSize: '2rem',
                    fontWeight: 700,
                    lineHeight: 1.15,
                    letterSpacing: '-0.02em',
                    margin: 0,
                    fontFamily: 'inherit',
                  }}
                >
                  Link gesendet
                </h1>
                <Text size="sm">
                  Wir haben dir einen Anmelde-Link an{' '}
                  <Text span fw={600} inherit>
                    {form.values.email}
                  </Text>{' '}
                  geschickt. Der Link läuft nicht ab und kann nur einmal verwendet werden.
                </Text>
                <Text size="sm" c="dimmed">
                  Keine E-Mail erhalten? Prüfe deinen Spam-Ordner oder{' '}
                  <Anchor
                    size="sm"
                    component="button"
                    type="button"
                    underline="always"
                    c={LINK_COLOR}
                    onClick={() => setState('idle')}
                  >
                    schick dir einen neuen Link
                  </Anchor>
                  .
                </Text>
              </Stack>
            ) : (
              <form onSubmit={form.onSubmit(handleSubmit)} aria-labelledby="customer-login-heading">
                <Stack gap="lg">
                  <div>
                    <h1
                      id="customer-login-heading"
                      style={{
                        fontSize: '2rem',
                        fontWeight: 700,
                        lineHeight: 1.15,
                        letterSpacing: '-0.02em',
                        margin: 0,
                        fontFamily: 'inherit',
                      }}
                    >
                      Anmelden
                    </h1>
                    <Text size="sm" mt={6} c="dimmed" style={{ textWrap: 'pretty' }}>
                      {showSsoButtons
                        ? 'Mit deinem Firmenkonto – oder per Link an deine E-Mail, ganz ohne Passwort.'
                        : 'Wir schicken dir einen Anmelde-Link an deine E-Mail – ganz ohne Passwort.'}
                    </Text>
                  </div>

                  {requestPending && (
                    <Alert
                      icon={<IconCheck size={16} />}
                      color="teal"
                      title="Zugangsanfrage gestellt"
                      radius="md"
                      withCloseButton
                      onClose={dismissUrlError}
                    >
                      <Stack gap="xs">
                        <Text size="sm" inherit>
                          Dein Konto wurde erkannt, hat aber noch keinen Portal-Zugang. Wir haben
                          eine Zugangsanfrage an das FixByte-Team gestellt — du bekommst eine
                          E-Mail, sobald dein Zugang freigeschaltet ist.
                        </Text>
                        {detailsToken && detailsState !== 'done' && (
                          <>
                            <Text size="sm" inherit fw={600}>
                              Hilf uns bei der Zuordnung — von welcher Firma bist du?
                            </Text>
                            <TextInput
                              size="sm"
                              label="Firma"
                              placeholder="z. B. Muster GmbH"
                              value={detailsCompany}
                              onChange={(e) => setDetailsCompany(e.currentTarget.value)}
                              disabled={detailsState === 'saving'}
                            />
                            <TextInput
                              size="sm"
                              label="Dein Name"
                              placeholder="Vor- und Nachname"
                              value={detailsName}
                              onChange={(e) => setDetailsName(e.currentTarget.value)}
                              disabled={detailsState === 'saving'}
                            />
                            {detailsState === 'error' && (
                              <Text size="xs" c="red">
                                Speichern fehlgeschlagen — bitte versuche es noch einmal.
                              </Text>
                            )}
                            <Button
                              size="xs"
                              color="teal"
                              loading={detailsState === 'saving'}
                              disabled={!detailsCompany.trim() && !detailsName.trim()}
                              onClick={() => void handleSubmitDetails()}
                            >
                              Angaben senden
                            </Button>
                          </>
                        )}
                        {detailsState === 'done' && (
                          <Text size="sm" inherit fw={600}>
                            Danke! Deine Angaben sind bei uns angekommen.
                          </Text>
                        )}
                      </Stack>
                    </Alert>
                  )}

                  {urlErrorMessage && (
                    <Alert
                      icon={<IconAlertCircle size={16} />}
                      color="red"
                      title="Anmeldung nicht möglich"
                      radius="md"
                      withCloseButton
                      onClose={dismissUrlError}
                    >
                      {urlErrorMessage}
                    </Alert>
                  )}

                  {state === 'error' && (
                    <Alert
                      icon={<IconAlertCircle size={16} />}
                      color="red"
                      title="Fehler"
                      radius="md"
                    >
                      {errorMsg || 'Ein unbekannter Fehler ist aufgetreten. Bitte versuche es erneut.'}
                    </Alert>
                  )}

                  {showSsoButtons && (
                    <>
                      <Stack gap="sm">
                        {sso.microsoft && (
                          <Button
                            component="a"
                            href="/public/customer/sso/microsoft/authorize"
                            onClick={startSso('microsoft')}
                            loading={ssoPending === 'microsoft'}
                            disabled={ssoPending === 'google'}
                            fullWidth
                            size="md"
                            variant="default"
                            radius="md"
                            leftSection={<IconBrandWindows size={18} color="#00A4EF" />}
                          >
                            Mit Microsoft anmelden
                          </Button>
                        )}
                        {sso.google && (
                          <Button
                            component="a"
                            href="/public/customer/sso/google/authorize"
                            onClick={startSso('google')}
                            loading={ssoPending === 'google'}
                            disabled={ssoPending === 'microsoft'}
                            fullWidth
                            size="md"
                            variant="default"
                            radius="md"
                            leftSection={<IconBrandGoogleFilled size={18} color="#4285F4" />}
                          >
                            Mit Google anmelden
                          </Button>
                        )}
                      </Stack>
                      <Divider label="oder per E-Mail-Link" labelPosition="center" />
                    </>
                  )}

                  <TextInput
                    label="E-Mail-Adresse"
                    placeholder="du@firma.de"
                    type="email"
                    size="md"
                    autoComplete="email"
                    autoFocus={!showSsoButtons}
                    leftSection={<IconMail size={18} />}
                    {...form.getInputProps('email')}
                    disabled={state === 'loading'}
                  />

                  <Button
                    type="submit"
                    fullWidth
                    size="md"
                    loading={state === 'loading'}
                    radius="md"
                    color="cyberOrange.7"
                    rightSection={<IconArrowRight size={18} />}
                  >
                    Anmelde-Link senden
                  </Button>

                  <Text size="sm" c="dimmed">
                    <Text span fw={600} c="dark.6" inherit>
                      Noch kein Zugang?
                    </Text>{' '}
                    {showSsoButtons
                      ? `Melde dich per ${ssoProviderNames} an – wir schalten dich nach kurzer Prüfung frei.`
                      : 'Dein Ansprechpartner bei FixByte richtet ihn dir ein.'}
                  </Text>
                </Stack>
              </form>
            )}
          </div>

          <LoginHelpFooter showSupportEmail={!hasSupportInfo} visibleFrom="md" mt={40} />
        </Box>
      </Box>

      <Box
        component="aside"
        aria-label="Über das Kundenportal"
        className={styles.infoPanel}
        // 50 % statt Basis 0: sonst teilt Flexbox nur den Rest nach Abzug der
        // (unterschiedlichen) Innenabstände, und die Infospalte wird breiter.
        flex={{ base: 1, md: '1 1 50%' }}
        px={{ base: 'lg', sm: 'xl', lg: 64 }}
        py={{ base: 40, md: 48 }}
        style={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}
      >
        <div className={styles.infoPattern} aria-hidden />
        <Stack className={styles.infoContent} w="100%" maw={{ base: 400, md: 480 }} gap={40}>
          <PortalHero visibleFrom="md" className={styles.fadeUp} />
          <div className={`${styles.fadeUp} ${styles.delay1}`}>
            <PortalFeatures />
          </div>
          {hasSupportInfo && support && (
            <div className={`${styles.fadeUp} ${styles.delay2}`}>
              <SupportContact support={support} whatsappHref={whatsappHref} />
            </div>
          )}
          <LoginHelpFooter showSupportEmail={!hasSupportInfo} hiddenFrom="md" />
        </Stack>
      </Box>
    </Flex>
  );
}
